import { currencyMetadata, displayDigitsFor } from './currency.ts'
import { formatScaled, rescale } from './decimal.ts'
import { MONEY_SCALE, type Money } from './money.ts'
import { RATE_SCALE, type Rate } from './rate.ts'
import { RATIO_SCALE, type Ratio } from './ratio.ts'
import type { Locale } from '../text/locale.ts'

export interface NumberFormat {
  readonly groupSeparator: string
  readonly decimalSeparator: string
}

// Pinned by hand: a WebView and a desktop browser are different ICU builds.
const NUMBER_FORMATS: Readonly<Record<Locale, NumberFormat>> = {
  en: { groupSeparator: ',', decimalSeparator: '.' },
  hu: { groupSeparator: ' ', decimalSeparator: ',' },
}

/** The group and decimal separators for a locale - what an amount input groups and splits on as the user types. */
export function numberFormatFor(locale: Locale): NumberFormat {
  return NUMBER_FORMATS[locale]
}

// U+2212, not the ASCII hyphen: measured in Geist at 14px semibold, '+' is 7.94px and
// '−' is 7.52px while '-' is 5.86px - mixing hyphens and pluses reads ragged on a column's
// left edge.
export const MINUS_SIGN = '−'
export const PLUS_SIGN = '+'

function group(digits: string, separator: string): string {
  let out = ''
  for (let i = digits.length; i > 0; i -= 3) {
    const chunk = digits.slice(Math.max(0, i - 3), i)
    out = out === '' ? chunk : `${chunk}${separator}${out}`
  }
  return out === '' ? '0' : out
}

/** Drops trailing zeros down to `floor` digits. */
function trimTrailingZeros(fraction: string, floor: number): string {
  let trimmed = fraction
  while (trimmed.length > floor && trimmed.endsWith('0')) trimmed = trimmed.slice(0, -1)
  return trimmed
}

function formatUnits(units: bigint, scale: number, locale: Locale, minimumFractionDigits: number): string {
  const negative = units < 0n
  const plain = formatScaled(negative ? -units : units, scale)
  const dot = plain.indexOf('.')
  const whole = dot === -1 ? plain : plain.slice(0, dot)
  const fraction = trimTrailingZeros(dot === -1 ? '' : plain.slice(dot + 1), minimumFractionDigits)
  const format = NUMBER_FORMATS[locale]
  const body = `${group(whole, format.groupSeparator)}${fraction === '' ? '' : `${format.decimalSeparator}${fraction}`}`
  return negative ? `${MINUS_SIGN}${body}` : body
}

export interface FormatMoneyOptions {
  readonly locale: Locale
  /**
   * A flow (an entry's amount, a period's income/expense/net) is signed: positive gets a
   * `+`, negative a `−`, zero neither. A level (a balance, net worth) is never signed.
   */
  readonly sign?: boolean
  readonly showSymbol?: boolean
}

/**
 * The one rendering of an amount: rounded to the currency's display digits (half away from
 * zero), both digits kept when there are cents (13,552.40€), none for a whole amount (500€),
 * locale grouping, the currency symbol in its own position with no gap. The wire's scale-4
 * value is never shown as-is.
 */
export function formatMoney(value: Money, options: FormatMoneyOptions): string {
  const digits = displayDigitsFor(value.currency)
  const rescaled = rescale(value.units, MONEY_SCALE, digits)
  const negative = rescaled < 0n
  const hasCents = rescaled % 10n ** BigInt(digits) !== 0n
  // Cents keep their two digits; a third display digit (KWD's fils) shows only when non-zero.
  const amount = formatUnits(negative ? -rescaled : rescaled, digits, options.locale, hasCents ? 2 : 0)
  const symbol = options.showSymbol === false ? '' : currencyMetadata(value.currency).symbol
  const before = currencyMetadata(value.currency).symbolPosition === 'before'
  const showPlus = options.sign === true && !negative && rescaled !== 0n
  const sign = negative ? MINUS_SIGN : showPlus ? PLUS_SIGN : ''
  return before ? `${sign}${symbol}${amount}` : `${sign}${amount}${symbol}`
}

/** True when the amount displays as zero at the currency's display digits: 0.004 EUR, 0.4 HUF. */
export function displaysAsZero(value: Money): boolean {
  return rescale(value.units, MONEY_SCALE, displayDigitsFor(value.currency)) === 0n
}

/**
 * The exact amount as a plain, locale-independent decimal string ('.' separator, no
 * grouping), for round-tripping into an editable field. Never rounded: an edit field must not
 * change the stored figure just by being opened.
 */
export function exactAmount(value: Money): string {
  return plainDecimal(value.units)
}

/**
 * {@link exactAmount} rounded to the currency's display digits (half away from zero): what an
 * edit field opens with, so a HUF figure never opens with forints' fractions. The stored value
 * is untouched - only the text an input starts from.
 */
export function openingAmount(value: Money): string {
  const digits = displayDigitsFor(value.currency)
  return plainDecimal(rescale(rescale(value.units, MONEY_SCALE, digits), digits, MONEY_SCALE))
}

function plainDecimal(units: bigint): string {
  const plain = formatScaled(units, MONEY_SCALE)
  const dot = plain.indexOf('.')
  if (dot === -1) return plain
  const fraction = trimTrailingZeros(plain.slice(dot + 1), 0)
  return fraction === '' ? plain.slice(0, dot) : `${plain.slice(0, dot)}.${fraction}`
}

export interface FormatRateOptions {
  readonly locale: Locale
  readonly minimumFractionDigits?: number
  readonly maximumFractionDigits?: number
}

export function formatRate(value: Rate, options: FormatRateOptions): string {
  const maximum = options.maximumFractionDigits ?? RATE_SCALE
  const minimum = Math.min(options.minimumFractionDigits ?? 2, maximum)
  const rescaled = rescale(value.units, RATE_SCALE, maximum)
  return formatUnits(rescaled, maximum, options.locale, minimum)
}

export interface FormatRatioOptions {
  readonly locale: Locale
  readonly minimumFractionDigits?: number
  readonly maximumFractionDigits?: number
}

/** No `%` or other unit suffix: a ratio's caller decides that, the same way a caption decides units for a chart axis. */
export function formatRatio(value: Ratio, options: FormatRatioOptions): string {
  const maximum = options.maximumFractionDigits ?? 2
  const minimum = Math.min(options.minimumFractionDigits ?? 2, maximum)
  const rescaled = rescale(value.units, RATIO_SCALE, maximum)
  return formatUnits(rescaled, maximum, options.locale, minimum)
}

/** The stored fraction (`0.25`) scaled up for percent display (`"25"`). No `%` suffix: same convention as {@link formatRatio}. */
export function formatRatioPercent(value: Ratio, options: FormatRatioOptions): string {
  return formatRatio({ units: value.units * 100n } as Ratio, options)
}
